import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

import { T, tokens } from './theme';
const API = 'https://tcio-api.onrender.com';
const LOC_COLOR = { DTX:'#B3402E', RTC:'#4479B0', PHP:'#9A6B00', IOP:'#4E6E92', OP:'#1E7F4F' };

// module-level cache so signed URLs aren't re-fetched per render
const urlCache = new Map();

export function usePatientPhoto(patientId, hasPhoto) {
  const [url, setUrl] = useState(urlCache.get(patientId) || null);
  useEffect(() => {
    if (!patientId || !hasPhoto) return;
    if (urlCache.has(patientId)) { setUrl(urlCache.get(patientId)); return; }
    let dead = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/emr/patients/${patientId}/photo`);
        if (!dead && r.data.url) { urlCache.set(patientId, r.data.url); setUrl(r.data.url); }
      } catch {}
    })();
    return () => { dead = true; };
  }, [patientId, hasPhoto]);
  return url;
}

export function PatientAvatar({ patient, size = 44, radius = 8 }) {
  const url = usePatientPhoto(patient.id, !!patient.photo_key);
  const initials = ((patient.first_name || ' ')[0] + (patient.last_name || ' ')[0]).toUpperCase();
  return (
    <div style={{ width:size, height:size, borderRadius:radius, overflow:'hidden', flexShrink:0,
      background:T.b100, border:`1px solid ${T.b200}`, display:'flex',
      alignItems:'center', justifyContent:'center' }}>
      {url ? <img src={url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
        : <span style={{ color:T.roseDeep, fontWeight:600,
            fontSize: size * 0.34 }}>{initials}</span>}
    </div>
  );
}

async function resizeImage(file, maxDim = 800) {
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result); r.onerror = rej;
    r.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = dataUrl;
  });
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.width * scale);
  canvas.height = Math.round(img.height * scale);
  canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', 0.85).split(',')[1];
}

export function PhotoUpload({ patientId, onDone, hasPhoto }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = () => ref.current && ref.current.click();
  const remove = async () => {
    if (!window.confirm('Remove this photo from the chart?')) return;
    setBusy(true);
    try { await axios.delete(`${API}/api/emr/patients/${patientId}/photo`); urlCache.delete(patientId); if (onDone) onDone(); }
    catch { alert('Could not remove the photo'); }
    setBusy(false);
  };
  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setBusy(true);
    try {
      const b64 = await resizeImage(file);
      await axios.post(`${API}/api/emr/patients/${patientId}/photo`,
        { image_base64: b64, content_type: 'image/jpeg' });
      urlCache.delete(patientId);
      if (onDone) onDone();
    } catch { alert('Photo upload failed'); }
    setBusy(false);
    e.target.value = '';
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/*" capture="environment"
        style={{ display:'none' }} onChange={onFile} />
      <button onClick={pick} disabled={busy}
        style={{ borderRadius:8, padding:'5px 12px', font:'inherit', fontWeight:600,
          fontSize:12, cursor:'pointer', background:'transparent', color:T.rose,
          border:`1px solid ${T.b300}` }}>
        {busy ? 'Uploading…' : (hasPhoto ? '📷 Replace' : '📷 Photo')}
      </button>
      {hasPhoto && (
        <button onClick={remove} disabled={busy} title="Remove this photo from the chart"
          style={{ borderRadius:8, padding:'5px 10px', font:'inherit', fontWeight:600, fontSize:12, cursor:'pointer',
            background:'transparent', color:T.muted || '#6b7280', border:`1px solid ${T.b300}`, marginLeft:6 }}>
          Remove
        </button>
      )}
    </>
  );
}

const SEV = {
  critical: { dot: T.blocking, bg: T.blockingBg, fg: T.blocking },
  warning:  { dot: T.warning,  bg: T.warningBg,  fg: T.warning },
  info:     { dot: T.info,     bg: T.infoBg,     fg: T.info },
  ok:       { dot: T.pass,     bg: T.passBg,     fg: T.pass },
};

function fmtDetail(d) {
  if (d && d.endsWith(' min')) {
    const mins = parseInt(d, 10);
    if (!isNaN(mins)) {
      if (mins >= 1440) return Math.round(mins / 1440) + ' days';
      if (mins >= 120) return Math.round(mins / 60) + ' hr';
      return mins + ' min';
    }
  }
  return d;
}

function PhotoCard({ p, att, onOpen }) {
  const [hov, setHov] = React.useState(false);
  const sev = att ? (SEV[att.severity] || SEV.info) : null;
  return (
    <div onClick={() => onOpen(p.id)} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ cursor:'pointer', background:T.surface, borderRadius:tokens.radiusCard, overflow:'hidden',
        border:'1px solid ' + (hov ? tokens.ai : T.b200),
        boxShadow: hov ? '0 3px 10px rgba(20,45,70,0.10)' : '0 1px 2px rgba(20,45,70,0.04)',
        transform: hov ? 'translateY(-1px)' : 'none',
        transition:'box-shadow .12s ease, border-color .12s ease, transform .12s ease',
        position:'relative', display:'flex', flexDirection:'column' }}>
      {sev ? <span style={{ position:'absolute', top:10, right:10, width:10, height:10, borderRadius:'50%',
        background:sev.dot, border:'2px solid rgba(255,255,255,0.92)', zIndex:2 }} /> : null}
      <GridPhoto patient={p} />
      <div style={{ padding:'10px 12px 8px' }}>
        <div style={{ fontWeight:600, fontSize:13.5, color:T.ink, lineHeight:1.25 }}>
          {p.last_name}, {p.first_name}
        </div>
        {p.discharge_date ? <div style={{ marginTop:3 }}><span style={{ fontSize:10, fontWeight:700, color:'#7f1d1d', background:'#fee2e2', border:'1px solid #fca5a5', borderRadius:10, padding:'2px 8px', whiteSpace:'nowrap', letterSpacing:'.02em' }}>DISCHARGED {new Date(p.discharge_date).toLocaleDateString('en-US',{month:'numeric',day:'numeric',timeZone:'UTC'})} {'\u00b7'} doc due</span></div> : null}
        <div style={{ fontFamily:'monospace', fontSize:11, color:T.inkSoft, marginTop:1 }}>{p.mrn}</div>
        <div style={{ fontSize:12, color:T.inkSoft, marginTop:4, lineHeight:1.35 }}>
          {p.house_name || p.facility_name || '-'}
          {p.loc ? <span style={{ color: LOC_COLOR[p.loc] || T.inkSoft, fontWeight:600 }}> · {p.loc}</span> : null}
          {p.day_of_stay ? <span> · Day {p.day_of_stay}</span> : null}
        </div>
      </div>
      <div style={{ marginTop:'auto' }}>
        {att ? (att.top ? (
          <div style={{ margin:'6px 10px 10px', padding:'9px 11px', borderRadius:8, background:sev.bg }}>
            <div style={{ fontSize:11.5, fontWeight:600, color:sev.fg, letterSpacing:'.01em', lineHeight:1.3,
              display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>
              {att.top.label}
            </div>
            {(att.top.detail || att.more_count > 0) ? (
              <div style={{ fontSize:11, color:T.inkSoft, marginTop:3, lineHeight:1.3 }}>
                {[att.top.detail ? fmtDetail(att.top.detail) : null,
                  att.more_count > 0 ? '+' + att.more_count + ' more' : null].filter(Boolean).join(' · ')}
              </div>
            ) : null}
          </div>
        ) : (
          <div style={{ margin:'6px 10px 10px', padding:'9px 11px', borderRadius:8, background:T.passBg,
            fontSize:12, color:T.pass, fontWeight:500 }}>✓ No urgent items</div>
        )) : null}
      </div>
    </div>
  );
}

export function PhotoGrid({ patients, onOpen, attention }) {
  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(180px, 1fr))', gap:12 }}>
      {patients.map(p => (
        <PhotoCard key={p.id} p={p} onOpen={onOpen}
          att={attention ? attention[p.id] : null} />
      ))}
    </div>
  );
}


function GridPhoto({ patient }) {
  const url = usePatientPhoto(patient.id, !!patient.photo_key);
  const initials = ((patient.first_name || ' ')[0] + (patient.last_name || ' ')[0]).toUpperCase();
  return (
    <div style={{ width:'100%', aspectRatio:'1 / 1', background:T.b100, display:'flex',
      alignItems:'center', justifyContent:'center', overflow:'hidden' }}>
      {url ? <img src={url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
        : <span style={{ color:T.b300, fontWeight:600,
            fontSize:44 }}>{initials}</span>}
    </div>
  );
}
